import { create } from 'zustand';

/**
 * Sketch tool UI bridge — session-only state for tools that need DOM input.
 *
 * The Text and 3D Text tools capture a model point through the viewport,
 * then hand off to a DOM overlay (SketchTextPrompt) for the actual string.
 * The overlay writes the committed text back here; the tool hook reads it
 * on the next click / Enter. Nothing here persists into the document.
 */

export interface SketchTextDraft {
    /** 'label' = 2D Text annotation; 'text3d' = extruded 3D Text solid. */
    kind: 'label' | 'text3d';
    anchor: [number, number, number];
    /** Viewport px where the prompt overlay should appear. */
    screen: { x: number; y: number };
    /** Draft text kept across the open prompt (typing continuity). */
    text: string;
    /** 3D Text options (height/depth) editable in the prompt. */
    height: number;
    depth: number;
}

/** A committed prompt: the overlay hands the string back to the tool. */
export interface SketchTextCommit {
    kind: 'label' | 'text3d';
    anchor: [number, number, number];
    text: string;
    height: number;
    depth: number;
}

interface SketchUiState {
    textDraft: SketchTextDraft | null;
    openTextDraft: (draft: Omit<SketchTextDraft, 'text' | 'height' | 'depth'> & Partial<SketchTextDraft>) => void;
    updateTextDraft: (patch: Partial<SketchTextDraft>) => void;
    closeTextDraft: () => void;
    /** Committed prompt pending tool consumption (null = consumed). */
    textCommit: SketchTextCommit | null;
    commitText: (commit: SketchTextCommit) => void;
    consumeTextCommit: () => SketchTextCommit | null;
}

export const useSketchUiStore = create<SketchUiState>((set, get) => ({
    textDraft: null,
    openTextDraft: (draft) =>
        set({
            textDraft: {
                ...draft,
                text: draft.text ?? '',
                height: draft.height ?? 1,
                depth: draft.depth ?? 0.25,
            } as SketchTextDraft,
        }),
    updateTextDraft: (patch) =>
        set((s) => (s.textDraft ? { textDraft: { ...s.textDraft, ...patch } } : {})),
    closeTextDraft: () => set({ textDraft: null }),
    textCommit: null,
    commitText: (commit) => set({ textCommit: commit }),
    consumeTextCommit: () => {
        const commit = get().textCommit;
        if (commit) set({ textCommit: null });
        return commit;
    },
}));
